import React, { useMemo, useState, useEffect, type FC } from 'react';
import { Layout, Flex, Typography, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import dayjs, { type Dayjs } from 'dayjs';
import { isValidGuid } from 'infrastructure/functions';
import { ModuleType, timePresetKeys } from 'infrastructure/enums';
import useUser from 'utils/useUser';
import TimeframePicker from 'components/molecules/oneplaform/timeframe-filter/timeframe-picker';
import DoNotCallTag from 'components/atoms/tags/DoNotCallTag';
import SlaTag from 'components/atoms/tags/SlaTag';
import EmailUnsubscribedTag from 'components/atoms/tags/EmailUnsubscribedTag';
import PatientChartNavSection from 'components/organisms/oneplatform/patient-chart-nav-section';
import PatientActionButtons from 'components/molecules/patientTopBanner/components/patient-action-buttons';
import PatientActionsDrawer from 'components/molecules/patientTopBanner/components/patient-actions-drawer';
import GeneralCommentModalNew from 'components/templates/rpm/patients/patient/generalComments/modal-new';
import { endOfDay, startOfDay } from 'date-fns';
import { useAccess } from 'infrastructure/hooks/access/useAccess';
import { useSendComment } from 'infrastructure/hooks/comments/use-send';
import { useGetDoNotCallInfo } from 'infrastructure/hooks/control-panel/use-get-call-info';
import { useBannerInfo } from 'components/molecules/patientTopBanner/hooks/useBannerInfo';

import styles from './styles.module.scss';
import SearchPatientsByType from '../search-patients-by-type';
import AppointmentList from '../rpm-ccm-navigation-header/appointment-list';
import { useRpmCcmHeader } from '../rpm-ccm-navigation-header/hooks/use-rpm-ccm-header';

import type { PresetKeyEnum } from 'infrastructure/enums';
import type { IRpmActivityCountResponse } from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { ICreateComment } from 'infrastructure/interfaces';

const { Header } = Layout;
const { Text } = Typography;

interface PatientChartHeaderProps {
  patientGuid: string;
  module?: ModuleType;
  clinicGuid?: string;
  goBackPath?: string;
  title?: string;
  patientName?: string;
  sla?: number;
  isShowEmailTag?: boolean;
  activityCount?: IRpmActivityCountResponse;
  showNavSection?: boolean;
  activeNavLink: string | null;
  onActiveNavLinkChange: (link: string | null) => void;
  isAutoScrollingRef: React.MutableRefObject<boolean>;
  hasNavInitializedRef?: React.MutableRefObject<boolean>;
}

const PatientChartHeader: FC<PatientChartHeaderProps> = ({
  patientGuid,
  module = ModuleType.RPMCCM,
  clinicGuid,
  goBackPath,
  title,
  patientName,
  sla,
  isShowEmailTag,
  activityCount,
  showNavSection = false,
  activeNavLink,
  onActiveNavLinkChange,
  isAutoScrollingRef,
  hasNavInitializedRef,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isPatient } = useUser();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isWideHeader, setIsWideHeader] = useState(false);
  const headerRef = React.useRef<HTMLElement>(null);

  const { isCombinedModule, isBlockCcmModule, isBlockRpmModule, patientInfo } =
    useAccess({ module, patientGuid });
  const { loading: commentLoading, sendComment } = useSendComment();
  const { doNotCallRpm, doNotCallCcm, rpmRestrictionType, ccmRestrictionType } =
    useGetDoNotCallInfo();

  const { holdingStatus, disabledCreateInternalAdmissionForms } = useBannerInfo(
    {
      patientGuid,
      module: ModuleType.CCM,
    },
  );

  const sendCommentHandler = async (data: ICreateComment) => {
    const response = await sendComment(data);
    setIsCommentsOpen(false);
    return response;
  };

  const {
    dataOptions,
    debounceSearch,
    isLoading,
    placeholder,
    options,
    dateRange,
    DEFAULT_FILTER,
    searchValue,
    searchKey,
    onCalendarChange,
    onApply,
    setSearchKey,
    setSearchValue,
    onSelect,
    DEFAULT_RANGE,
    setDateRange,
  } = useRpmCcmHeader({ module, clinicGuid, patientGuid });

  const selectedRange = useMemo<[Dayjs | null, Dayjs | null]>(() => {
    if (!dateRange) return [null, null];
    return [dayjs(dateRange[0]), dayjs(dateRange[1])];
  }, [dateRange]);

  const handleRangeChange = (
    dates: null | (Dayjs | null)[],
    dateStrings: string[],
    presetKey?: string | null,
  ) => {
    if (!dates || !dates[0] || !dates[1]) {
      onCalendarChange(
        [null, null],
        ['', ''],
        { range: undefined },
        presetKey || undefined,
      );
      return;
    }
    const isTimePreset =
      !!presetKey && timePresetKeys.includes(presetKey as PresetKeyEnum);
    const start = isTimePreset
      ? dates[0].toDate()
      : startOfDay(dates[0].toDate());
    const end = isTimePreset ? dates[1].toDate() : endOfDay(dates[1].toDate());
    onCalendarChange(
      [start, end],
      [dateStrings[0], dateStrings[1]],
      { range: undefined },
      presetKey || undefined,
    );
    onApply([start, end]);
  };

  const goBack = () => {
    const canGoBack = location.key !== 'default';
    const hasHash = location.hash;
    if (canGoBack && !hasHash) {
      navigate(-1);
    } else if (goBackPath) {
      navigate(goBackPath);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const headerElement = headerRef.current;
    if (!headerElement) return;

    const checkWidth = () => {
      const headerWidth = headerElement.offsetWidth;
      const maxWidthStr = getComputedStyle(document.documentElement)
        .getPropertyValue('--max-app-width')
        .trim();
      const maxWidth = parseInt(maxWidthStr, 10);

      if (!Number.isNaN(maxWidth)) {
        setIsWideHeader(headerWidth > maxWidth);
      }
    };

    checkWidth();

    const resizeObserver = new ResizeObserver(checkWidth);
    resizeObserver.observe(headerElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const pageTitle = title || t('labels.patientChart', 'Patient Chart');

  return (
    <Header
      ref={headerRef}
      className={classNames(styles['breadcrumb-bar'], {
        [styles.scrolled]: isScrolled,
        [styles['show-nav']]: showNavSection,
      })}
    >
      <Flex
        align="center"
        gap={8}
        className={styles['breadcrumb-bar-inner']}
        justify="space-between"
      >
        {!isScrolled ? (
          <>
            <Flex align="center" gap={16}>
              {!isPatient && goBackPath && (
                <Button
                  icon={<ArrowLeftOutlined />}
                  size="middle"
                  className={styles.backButton}
                  onClick={goBack}
                  data-cy="go-back-btn"
                />
              )}
              <Text className={styles.pageTitle}>{pageTitle}</Text>
            </Flex>

            <div
              className={classNames(styles['controls-wrapper'], {
                [styles['for-patient']]: isPatient,
              })}
            >
              <div className={styles.label} />
              <div
                data-cy="report-time-window"
                className={styles['date-wrapper']}
              >
                <TimeframePicker
                  selectedRange={selectedRange}
                  minRangeDays={0}
                  onRangeChange={handleRangeChange}
                  popupClassName="timeframe-range-picker-popup"
                  clearSelection={() => {
                    const resetRange: [Date, Date] = [
                      startOfDay(DEFAULT_RANGE[0]),
                      endOfDay(DEFAULT_RANGE[1]),
                    ];
                    setDateRange(resetRange);
                    onApply(resetRange);
                  }}
                  panelRenderWrapper={(
                    children: React.ReactNode,
                    draftRange: [Dayjs | null, Dayjs | null],
                  ) => (
                    <AppointmentList
                      dateRange={
                        draftRange[0] && draftRange[1]
                          ? [draftRange[0].toDate(), draftRange[1].toDate()]
                          : dateRange
                      }
                      patientGuid={patientGuid}
                    >
                      {children}
                    </AppointmentList>
                  )}
                />
              </div>
              {!isPatient && (
                <div className={styles['search-wrapper']}>
                  <SearchPatientsByType
                    typeOptions={options}
                    defaultTypeValue={DEFAULT_FILTER.value}
                    dataOptions={dataOptions ?? []}
                    typeValue={searchKey}
                    searchValue={searchValue}
                    isLoading={isLoading}
                    placeholder={placeholder}
                    searchWith={300}
                    onClear={() => {
                      setSearchValue('');
                    }}
                    onTypeChange={(v) => {
                      setSearchValue('');
                      setSearchKey(v);
                    }}
                    onSelect={onSelect}
                    onSearch={(e) => {
                      setSearchValue(e.target.value);
                      const isGuid = searchKey === 'guid';
                      if (isGuid && isValidGuid(e.target.value)) {
                        debounceSearch({
                          searchKey,
                          searchValue: e.target.value,
                        });
                      } else if (!isGuid) {
                        debounceSearch({
                          searchKey,
                          searchValue: e.target.value,
                        });
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <Flex align="center" gap={8} className={styles['compact-left']}>
              {!isPatient && goBackPath && (
                <Button
                  icon={<ArrowLeftOutlined />}
                  size="middle"
                  className={styles.backButton}
                  onClick={goBack}
                  data-cy="go-back-btn"
                />
              )}
              <Flex align="center" gap={12} className={styles['compact-info']}>
                <Text className={styles['compact-name']}>
                  {patientName || pageTitle}
                </Text>
                <Flex align="center" gap={8} className={styles['compact-tags']}>
                  {doNotCallRpm && (
                    <DoNotCallTag
                      module="RPM"
                      restrictionType={rpmRestrictionType}
                      small
                    />
                  )}
                  {doNotCallCcm && (
                    <DoNotCallTag
                      module="CCM"
                      restrictionType={ccmRestrictionType}
                      small
                    />
                  )}
                  {sla && <SlaTag hours={sla} small />}
                  {isShowEmailTag && <EmailUnsubscribedTag small />}
                </Flex>
              </Flex>
            </Flex>
            <Flex align="center" gap={8} className={styles['compact-actions']}>
              <PatientActionButtons
                patientGuid={patientGuid}
                module={module}
                onComment={() => setIsCommentsOpen(true)}
                onMore={() => setIsDrawerOpen(true)}
                wrapper="none"
              />
            </Flex>
          </>
        )}
      </Flex>
      {showNavSection && (
        <div
          className={classNames(styles['nav-section-wrapper'], {
            [styles.wide]: isWideHeader,
          })}
        >
          <PatientChartNavSection
            module={module}
            activityCount={activityCount}
            isInHeader
            activeLink={activeNavLink}
            onActiveLinkChange={onActiveNavLinkChange}
            isAutoScrollingRef={isAutoScrollingRef}
            hasInitializedRef={hasNavInitializedRef}
          />
        </div>
      )}

      <GeneralCommentModalNew
        isOpen={isCommentsOpen}
        toggle={setIsCommentsOpen}
        patientGuid={patientGuid}
        loading={commentLoading}
        submit={sendCommentHandler}
        module={module}
        interactionStatus={patientInfo?.interactionStatus}
        tabsProps={{
          showTabs: isCombinedModule,
          disableCcm: isBlockCcmModule,
          disableRpm: isBlockRpmModule,
        }}
      />

      <PatientActionsDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        patientGuid={patientGuid}
        module={module}
        clinicGuid={clinicGuid}
        emailSubscription={patientInfo?.emailSubscription}
        ccmHoldingStatus={holdingStatus?.status !== 'active'}
        disabledCreateInternalAdmissionForms={
          disabledCreateInternalAdmissionForms
        }
      />
    </Header>
  );
};

export default PatientChartHeader;
